import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Alert,
  Center,
  Loader,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";

import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useCellRender } from "../../hooks/useCellRender";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { resolveEntityPath } from "../../utils/resolveEntityPath";
import { EDD_INDICATOR_LABELS } from "../../helpers/eddIndicatorLabels";

const ClientLink = ({ row, onOpen }) => {
  const code = row.clientCode || row.code;
  return (
    <Text
      component="a"
      size="sm"
      style={{ textDecoration: "none", cursor: "pointer" }}
      onClick={(event) => onOpen(event, row)}
    >
      {code || row.name || "-"}
      {row.name ? (
        <Text component="span" color="dimmed" size="xs">
          {" "}
          {row.name}
        </Text>
      ) : null}
    </Text>
  );
};

const EddExceptionReport = () => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [cellRender] = useCellRender();
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const clientPath = resolveEntityPath("client", auth()?.userRole);
  const money = (value) => cellRender.Currency(value, "HKD");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await apiRef.current.Client.getEddExceptionReport();
      if (cancelled) return;
      if (result?.success) setReport(result.data);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const openClient = (event, row) => {
    event.preventDefault();
    if (!row?.clientId || !clientPath) return;
    if (tabNav.enabled) {
      tabNav.openDetail({
        pathname: clientPath,
        id: row.clientId,
        mode: "edit",
        label: row.clientCode || row.code || row.name,
      });
      return;
    }
    window.open(`${clientPath}?id=${row.clientId}&mode=edit`, "_blank");
  };

  const basic = report?.basic || [];
  const closest = report?.backup?.closestTo2M || [];
  const scores = report?.backup?.highestScore || [];
  const withdrawals = report?.withdrawals?.top || [];

  return (
    <Page title="EDD Exception Report">
      <Text size="sm" color="dimmed" mb="lg">
        {t(
          "Suspicious transaction indicators for enhanced due diligence. Backup picks are shown only when nobody meets the matching basic rule."
        )}
      </Text>
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : !report ? (
        <Alert color="red">{t("Unable to load the exception report.")}</Alert>
      ) : (
        <>
          <Title order={5} mb="sm">
            {t("Basic criteria")}
          </Title>
          {basic.length === 0 ? (
            <Text size="sm" color="dimmed" mb="xl">
              {t("No client currently meets a basic EDD indicator.")}
            </Text>
          ) : (
            <Table fontSize="sm" striped highlightOnHover mb="xl">
              <thead>
                <tr>
                  <th>{t("Client")}</th>
                  <th>{t("Indicator")}</th>
                  <th>{t("Life/ILAS NB")}</th>
                  <th>{t("Lump Sum Premium")}</th>
                  <th>{t("Subscriptions")}</th>
                  <th>{t("Subscription Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {basic.map((row) => (
                  <tr key={row.clientId}>
                    <td>
                      <ClientLink row={row} onOpen={openClient} />
                    </td>
                    <td>
                      {(row.reasons || []).map((key) => (
                        <Text key={key} size="xs">
                          {t(EDD_INDICATOR_LABELS[key] || key)}
                        </Text>
                      ))}
                    </td>
                    <td>{row.lifeIlas?.nbCount12Months ?? 0}</td>
                    <td>{money(row.lifeIlas?.lumpSumPremiumHkd)}</td>
                    <td>{row.subscription?.count ?? 0}</td>
                    <td>{money(row.subscription?.totalAmountHkd)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Title order={5} mb="sm">
            {t("Backup: closest to HKD 2M")}
          </Title>
          {closest.length === 0 ? (
            <Text size="sm" color="dimmed" mb="xl">
              {t("Not used. At least one subscription amount exceeds HKD 2M.")}
            </Text>
          ) : (
            <Table fontSize="sm" striped highlightOnHover mb="xl">
              <thead>
                <tr>
                  <th>{t("Client")}</th>
                  <th>{t("Subscriptions")}</th>
                  <th>{t("Subscription Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {closest.map((row) => (
                  <tr key={`close-${row.clientId}`}>
                    <td>
                      <ClientLink row={row} onOpen={openClient} />
                    </td>
                    <td>{row.count}</td>
                    <td>{money(row.totalAmountHkd)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Title order={5} mb="sm">
            {t("Backup: highest count times amount")}
          </Title>
          {scores.length === 0 ? (
            <Text size="sm" color="dimmed" mb="xl">
              {t(
                "Not used. At least one client has more than 5 subscriptions and more than HKD 1M."
              )}
            </Text>
          ) : (
            <Table fontSize="sm" striped highlightOnHover mb="xl">
              <thead>
                <tr>
                  <th>{t("Client")}</th>
                  <th>{t("Subscriptions")}</th>
                  <th>{t("Subscription Amount")}</th>
                  <th>{t("Score")}</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((row) => (
                  <tr key={`score-${row.clientId}`}>
                    <td>
                      <ClientLink row={row} onOpen={openClient} />
                    </td>
                    <td>{row.count}</td>
                    <td>{money(row.totalAmountHkd)}</td>
                    <td>{money(row.score)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Title order={5} mb="sm">
            {t("Top 5 withdrawals")} ({report.withdrawals?.month || moment().format("YYYY-MM")})
          </Title>
          {withdrawals.length === 0 ? (
            <Text size="sm" color="dimmed">
              {t("No withdrawals this month.")}
            </Text>
          ) : (
            <Table fontSize="sm" striped highlightOnHover>
              <thead>
                <tr>
                  <th>{t("Rank")}</th>
                  <th>{t("Client")}</th>
                  <th>{t("Service")}</th>
                  <th>{t("Order")}</th>
                  <th>{t("Date")}</th>
                  <th>{t("Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((row) => (
                  <tr key={row._id || `${row.clientId}-${row.rank}`}>
                    <td>{row.rank}</td>
                    <td>
                      <ClientLink row={row} onOpen={openClient} />
                    </td>
                    <td>{row.code || "-"}</td>
                    <td>{row.orderCode || "-"}</td>
                    <td>
                      {row.date ? moment(row.date).format("YYYY-MM-DD") : "-"}
                    </td>
                    <td>{money(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Page>
  );
};

export default EddExceptionReport;
